/**
 * Reporting form composer shape + defaults (shared by Redux + localStorage restore).
 */

import {
  createInitialDescriptionBlocks,
  ReportDescriptionBlock,
} from './reportDescriptionBlocks';
import { ReportAuditSet } from './reportAuditSets';
import { ReportChartsMetricMode } from './reportMetrics';
import { ReportContextUser } from './reportContextUsers';

/** New reports: details first, then body. Replies start on body. */
export type ReportingComposerPhase = 'details' | 'body';

export type PreparedReportDraft = {
  id: string,
  viewUrl?: string | null,
  preview?: string,
};

/** Composer form — kept in Redux + localStorage so refresh restores it (per staff, not shared). */
export type ReportingComposerState = {
  toEmail: string,
  /** Mandatory one-line subject for the report. */
  subject: string,
  descBlocks: ReportDescriptionBlock[],
  activeTextBlockId: string,
  includeAudits: boolean,
  /** Frozen Audits snapshots pinned for this report (serial 1..n). */
  auditSets: ReportAuditSet[],
  /**
   * @deprecated Prefer ReportAuditSet.showCharts — kept so old localStorage
   * can seed per-set toggles on restore.
   */
  showCharts: boolean,
  /** Counts vs balances chart family (rightbar). */
  chartsMetricMode: ReportChartsMetricMode,
  previewPage: number,
  draft: PreparedReportDraft | null,
  /** Inbox report being answered — locks Send to / Purpose. */
  replyToReportId: string | null,
  replyToFromName: string,
  /** New-report wizard step. Replies always use body. */
  composerPhase: ReportingComposerPhase,
  /** Up to 5 staff named in this report, each Favorable / Neutral / Unfavorable. */
  contextUsers: ReportContextUser[],
  /** Required when any in-context person is Favorable or Unfavorable. */
  alertLevelSummary: string,
};

export const createDefaultReportingComposer = (): ReportingComposerState => ({
  toEmail: '',
  subject: '',
  descBlocks: createInitialDescriptionBlocks(),
  activeTextBlockId: '',
  includeAudits: false,
  auditSets: [],
  showCharts: true,
  chartsMetricMode: 'balances',
  previewPage: 1,
  draft: null,
  replyToReportId: null,
  replyToFromName: '',
  composerPhase: 'details',
  contextUsers: [],
  alertLevelSummary: '',
});
